<template>
	<div class="search_pop_wrap">
		<transition name="slideIn" @enter="onEnter" @leave="onLeave">
			<div v-show="showsFinder" class="search_pop">
				<div class="search_area">
					<button type="button" class="close" @click="close">
						<i class="ico24_close"></i>
						<span class="txt_hidden">닫기</span>
					</button>

					<section class="search_field">
						<h2>어떤 상품을 찾으세요?</h2>

						<div class="search_wrap">
							<InputText
								ref="keywordElement"
								v-model.trim="keyword"
								type="search"
								class="search"
								clearable
								:placeholder="marketingKeyword"
								@keyup.enter="onEnterSearch"
							/>
							<button type="submit" class="btn_search" @click="onSearch"><span class="txt_hidden">검색</span></button>
						</div>
					</section>

					<div class="list_wrap" ref="scrollWrapElement">
						<section class="recent">
							<div class="title">
								<strong>최근 검색어</strong>
								<button type="button" class="btn btn_txt link" @click="clearRecentKeywords">
									<span class="txt">전체삭제</span>
								</button>
							</div>

							<ul v-if="recentKeywords.length">
								<li v-for="item in recentKeywords" :key="item.keyword">
									<RouterLink
										:to="{
											path: '/search',
											query: {
												searchWord: item.keyword,
												isMarketing: false,
											},
										}"
										@click.native="
											close();
											crmSearchResult(item.keyword);
										"
									>
										<span>{{ item.keyword }}</span>
									</RouterLink>
									<button type="button" class="btn icon_only" @click="removeRecentKeywords(item.keyword.trim())">
										<i class="ico16_close_n" />
										<span class="txt_hidden">삭제</span>
									</button>
								</li>
							</ul>

							<div v-if="!recentKeywords.length" class="no_data empty">최근 검색어가 없습니다.</div>
						</section>

						<section class="popular">
							<div class="title">
								<strong>인기 검색어</strong>
							</div>
							<ol>
								<li v-for="item in bestKeywords" :key="item.searchWordSeq">
									<RouterLink
										:to="{
											path: '/search',
											query: {
												searchWord: item.searchWord,
												isMarketing: false,
											},
										}"
										@click.native="close"
									>
										{{ item.searchWord }}
									</RouterLink>
								</li>
							</ol>
						</section>
					</div>
				</div>
				<div class="search_dim" :class="{ visible: showsFinder }" @click="close"></div>
			</div>
		</transition>
		<!-- dim -->
	</div>
</template>

<script>
import { computed, defineComponent, ref, unref, watch, nextTick } from '@vue/composition-api';
import { useRouter } from '@/utils/hooks';
import { useStore } from '@vueblocks/vue-use-vuex';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';

import { InputText } from '@/components/common/Inputs';

import useMarketingKeywordsAPI from './useMarketingKeywordsAPI';
import useSearchWordListAPI from './useSearchWordListAPI';
import AlarmMsg from '@/utils/alarmMsg';

import { crmSearchResult } from '@/helpers/ad/crm/crm.js';

export default defineComponent({
	components: {
		InputText,
	},

	setup() {
		const router = useRouter();
		const store = useStore();
		const scrollWrapElement = ref();
		const keywordElement = ref();

		const defaultKeyword = computed(() => store.getters['finder/defaultKeyword']);
		const keyword = ref(unref(defaultKeyword) ?? '');

		const marketingKeywordAPI = useMarketingKeywordsAPI();
		const searchWordListAPI = useSearchWordListAPI();

		const marketingKeyword = computed(() => unref(marketingKeywordAPI.data)?.[0]?.mktgSearchWord);
		const marketingSeq = computed(() => unref(marketingKeywordAPI.data)?.[0]?.mktfSearchWordSeq);

		const increaseSearchKey = () => store.dispatch('finder/increaseKey');

		const close = () => {
			store.dispatch('finder/closeFinder');
			store.dispatch('finder/increaseKey');
		};
		const search = (keyword, { isMarketing = false, marketingSeq } = {}) => {
			if (!keyword) return;
			close();
			increaseSearchKey();

			/** crm 상단메뉴 - 검색 */
			crmSearchResult(keyword);

			router.push({
				path: '/search',
				query: {
					searchWord: keyword,
					isMarketing,
					marketingSeq,
				},
			});
		};

		const showsFinder = computed(() => store.getters['finder/showsFinder']);

		watch(showsFinder, value => {
			if (!value) return;
			nextTick(() => {
				unref(keywordElement)?.focus?.();
			});

			keyword.value = unref(defaultKeyword) ?? keyword.value;
		});

		return {
			showsFinder,
			close,

			keywordElement,
			keyword,
			recentGoods: computed(() => store.getters['recent/recentGoods']),
			recentKeywords: computed(() => store.getters['recent/recentKeywords']),

			bestKeywords: computed(() => unref(searchWordListAPI.data) || []),
			marketingKeyword,

			removeRecentKeywords: keyword => store.dispatch('recent/removeRecentKeywords', keyword),
			clearRecentKeywords: () => store.dispatch('recent/clearRecentKeywords'),
			search,

			onSearch: () => {
				if (!keyword.value) {
					marketingKeyword.value
						? search(marketingKeyword.value, { isMarketing: true, marketingSeq: unref(marketingSeq) })
						: AlarmMsg.showMsg('검색어를 입력해주세요.');
					return;
				}

				if (keyword.value.length < 2) {
					AlarmMsg.showMsg('검색어는 최소 2글자를 입력해주세요.');
					return;
				}

				search(keyword.value);
				document.activeElement?.blur();
			},

			onEnterSearch: () => {
				if (!keyword.value) {
					AlarmMsg.showMsg('검색어를 입력해주세요.');
					return;
				}

				if (keyword.value.length < 2) {
					AlarmMsg.showMsg('검색어는 최소 2글자를 입력해주세요.');
					return;
				}

				search(keyword.value);
				document.activeElement?.blur();
			},

			scrollWrapElement,
			onEnter: () => {
				disableBodyScroll(scrollWrapElement.value);
			},
			onLeave: () => {
				enableBodyScroll(scrollWrapElement.value);
			},
			crmSearchResult,
		};
	},
});
</script>
